/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { HttpHeaders } from '@angular/common/http';
import { type OnInit, Component, Input, ViewChild } from '@angular/core';
import { type BaseRequestOptionsArgs, type SelectOption, FormGroup, PaginatorComponent, TIME_FORMATTER, TimeService, UNPLACEHOLDER } from '@dm/core';
import { SWITCH } from 'app/config/switch';
import QRCode from 'qrcode';

import { EventKeyDownKeyCode, FIRST_PAGE_INDEX, PlayTimeInterval, STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH } from '../export.const';
import { type StreamlineDiagnostic, LogType, StreamlineDiagnosticShowWayTypeE } from '../export.enum';
import { ExportStrService } from '../export-str.service';
import { StreamlineDiagnosticService } from './streamline-dianostic.service';
@Component({
    selector: 'app-streamline-dianostic',
    templateUrl: './streamline-dianostic.component.html',
    styleUrls: ['./streamline-dianostic.component.scss'],
})

export class StreamlineDiagnosticComponent implements OnInit {
    @Input() encryptionVm: FormGroup;

    @ViewChild('paginator') paginator: PaginatorComponent;
    constructor(
        public str: ExportStrService,
        public service: StreamlineDiagnosticService,
        public timeService: TimeService,
    ) { }

    /**
     * 二维码是否全屏展示
     */
    isQrcodeFullscreenDisplay = false;

    /**
     * 忙等
     */
    loading = false;

    /**
     * i18n
     */
    i18n = this.str.i18nLabel;

    /**
     * 是否无数据
     */
    isQueryFalse = false;

    /**
     * 获取到的关键信息（base64编码）
     */
    streamlineDiagnostic: string;

    /**
     * 展示方式：文本/二维码
     */
    StreamlineDiagnosticShowWayTypeE = StreamlineDiagnosticShowWayTypeE;
    streamlineDiagnosticShowWay = StreamlineDiagnosticShowWayTypeE.QRCODE;

    /**
     * 播放速度选项配置,默认0.3s每张
     */
    playSpeedOptions: SelectOption[] = [];
    selectedPlaySpeed = PlayTimeInterval.THREE_HUNDRED_MIllISECONDS;

    /**
     * 二维码数组
     */
    qrImageArr: string[] = [];

    /**
     * 自动播放定时器
     */
    playTimer$: NodeJS.Timeout;

    /**
     * 二维码展示内容最大长度
     */
    STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH = STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH;

    /**
     * 分页器配置
     */
    currentPage = FIRST_PAGE_INDEX;
    paginatorOffset = 0;
    paginatorLimit = STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH;

    /**
     * 是否首次收集
     */
    isFirstCollect = true;

    /**
     * 收集时获取的文件名称
     */
    fileName: string;

    /**
     * 收集文件时间
     */
    fileDateStr: string;

    /**
     * 文件是否不能导出(每次文件收集仅可导出一次)
     */
    isFileNonexportable: boolean;

    LogType = LogType;

    /**
     * 二维码边框背景图url
     */
    qrcodeBorderBackgroundImageUrl = SWITCH.IS_SIMPLE_MODE ?
        '../../../../../../assets/svg/streamline-dianostic-qrcode-border-simple.svg' : '../../../../../../assets/svg/streamline-dianostic-qrcode-border.svg';

    SWITCH = SWITCH;

    ngOnInit(): void {
        this.playSpeedOptions = this.str.playSpeedOptionsOptions;
        this.getData();
    }

    /**
     * 改变二维码展示方式（是否放大全屏）
     */
    changeQrcodeShow(): void {
        if (!this.isQrcodeFullscreenDisplay) {
            window.addEventListener('keydown', this.keyBoardListen);
            this.isQrcodeFullscreenDisplay = true;
            this.startAutoPlay();
            return;
        }
        this.stopAutoPlay();
        window.removeEventListener('keydown', this.keyBoardListen);
        this.isQrcodeFullscreenDisplay = false;
    }

    /**
     * 重新收集刷新刷新数据
     */
    updateData(): void {
        this.loading = true;

        const options: BaseRequestOptionsArgs = {
            headers: new HttpHeaders({ fileEncryptionKey: this.encryptionVm?.value?.key }),
        };

        const isEncryptionWhenCollect = this.encryptionVm?.value?.encryption;

        this.service.collectStreamlineDiagnostic(isEncryptionWhenCollect, options).subscribe(
            ({ data }: { data: StreamlineDiagnostic }) => {
                this.loading = false;
                if (!data) {
                    this.isQueryFalse = true;
                    return;
                }
                this.handleData(data);
            }, () => {
                this.isQueryFalse = true;
                this.loading = false;
            });

        // 首次收集赋为false
        if (this.isFirstCollect) {
            this.isFirstCollect = false;
        }
    }

    /**
     * 刚进入查询数据
     */
    getData(): void {
        this.loading = true;

        this.service.queryStreamlineDiagnostic().subscribe(
            ({ data }: { data: StreamlineDiagnostic }) => {
                this.loading = false;
                if (!data) {
                    this.isQueryFalse = true;
                    return;
                }

                // 均为空，为未收集数据
                this.isFirstCollect = !(data.date && data.file_name && data.simplified_diagnose_info);
                if (this.isFirstCollect) {
                    this.isQueryFalse = false;
                    return;
                }

                this.handleData(data);
            }, () => {
                this.isQueryFalse = true;
                this.loading = false;
            });

        // 首次收集赋为false
        if (this.isFirstCollect) {
            this.isFirstCollect = false;
        }
    }

    /**
     * 处理数据
     */
    handleData(data: StreamlineDiagnostic): void {
        if (!data.simplified_diagnose_info) {
            this.loading = false;
            this.isQueryFalse = true;
            return;
        }
        this.fileName = String(data.file_name);
        this.streamlineDiagnostic = String(data.simplified_diagnose_info);
        this.updateQrcode(data.simplified_diagnose_info);
        const date = data.date ? this.timeService.formatUTC(Number(data.date) * 1000, TIME_FORMATTER.DEFAULT, false) : UNPLACEHOLDER;
        this.fileDateStr = `${this.i18n.timeLab} ${date}`;

        // 每次更新数据成功代表有新文件未导出
        this.isFileNonexportable = false;

        // 刷新数据页面页数置为1
        this.paginator.goPage(FIRST_PAGE_INDEX);
        this.isQueryFalse = false;
    }

    /**
     * 刷新二维码
     */
    async updateQrcode(qrcodeStr: string): Promise<void> {
        const qrcodeImgCount = Math.ceil(this.streamlineDiagnostic.length / STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH);
        this.qrImageArr = [];
        for (let i = 0; i < qrcodeImgCount; i++) {
            if (STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH * i > this.streamlineDiagnostic.length) {
                this.qrImageArr.push(await QRCode?.toString(qrcodeStr.slice(i * STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH), { type: 'svg', mode: 'M' }));
                return;
            }
            this.qrImageArr.push(await QRCode?.toString(qrcodeStr.slice(i * STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH, (i + 1) * STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH), { type: 'svg', mode: 'M' }));
        }
    }

    /**
     * 改变关键信息展示方式
     */
    changeStreamlineDiagnosticShowWay(showWay: StreamlineDiagnosticShowWayTypeE): void {
        this.streamlineDiagnosticShowWay = showWay;
    }

    /**
     * 页码发生变化，手动点击触发或者轮播触发
     */
    onPageChange(event: { offset: number; limit: number }): void {
        const { offset } = event;
        this.paginatorOffset = offset;
    }

    /**
     * 开始二维码自动播放
     */
    startAutoPlay = (): void => {
        const playTime = this.selectedPlaySpeed || PlayTimeInterval.THREE_HUNDRED_MIllISECONDS;
        if (!this.playTimer$) {
            this.playTimer$ = setInterval(() => {
                this.nextQrcodePage();
            }, playTime);
        }
    };

    /**
     * 停止二维码自动播放
     */
    stopAutoPlay(): void {
        clearInterval(this.playTimer$);
        this.playTimer$ = null;
    }

    /**
     * 获取轮播图背景图片
     */
    getImageStr = (page: number): string => this.qrImageArr[page];

    /**
     * 改变播放速度
     */
    changePlaySpeed(): void {
        if (!this.playTimer$) {
            return;
        }
        this.stopAutoPlay();
        this.startAutoPlay();
    }

    /**
     * 键盘监听事件
     * 快捷键,空格暂停/继续播放，左右切换上一张/下一张且停止轮播
     */
    keyBoardListen = (event: KeyboardEvent): void => {
        const keyboardTag = event.keyCode;
        if (keyboardTag === EventKeyDownKeyCode.ARROW_LEFT) {
            this.paginator.prev();
            this.stopAutoPlay();
        }

        if (keyboardTag === EventKeyDownKeyCode.ARROW_RIGHT) {
            this.paginator.next();
            this.stopAutoPlay();
        }

        if (keyboardTag === EventKeyDownKeyCode.SPACE) {
            if (this.playTimer$) {
                this.stopAutoPlay();
            } else {
                this.startAutoPlay();
            }
        }
    };

    /**
     * 轮播下一张二维码
     */
    nextQrcodePage(): void {
        const offset = this.paginator.currentPage * STREAMLINE_DIAGNOSTIC_QRCODE_MAX_LENGTH;

        // 轮播到最后一张后从第一张开始
        if (offset > this.streamlineDiagnostic.length) {
            this.paginator.goPage(FIRST_PAGE_INDEX);
            return;
        }

        this.paginator.next();
    }

    /**
     * 复制文本
     */
    copyText(): void {
        navigator.clipboard.writeText(this.streamlineDiagnostic);
    }

    /**
     * 导出相关文件
     */
    export(): void {
        this.service.queryExportStreamlineDiagnostic().subscribe(
            () => {
                this.service.exportStreamlineDiagnostic(this.fileName).subscribe(() => {
                    this.isFileNonexportable = true;
                });
            });
    }

    /**
     * 点击收集按钮
     */
    clickCollectBtn(): void {
        // 如果勾选加密先进行表单校验
        if (this.encryptionVm?.value?.encryption) {
            this.encryptionVm.validate((valid: boolean) => {
                if (!valid) {
                    return;
                }
                this.updateData();
            });
            return;
        }

        this.updateData();
    }
}
